import * as THREE from 'three';
import { ProjectilePool } from '../weapons/ProjectilePool';
import { EventBus } from '../core/EventBus';

export interface TankConfig {
  position: THREE.Vector3;
  targetRef: THREE.Object3D;
  health?: number;
  speed?: number;
  damage?: number;
  fireRate?: number;
}

const DEFAULT_CONFIG = {
  health: 200,
  speed: 5,
  damage: 25,
  fireRate: 0.4, // shots per second (slow but powerful)
};

let tankIdCounter = 0;

export class HeavyTank {
  public readonly id: string;
  public readonly mesh: THREE.Group;

  private config: Required<TankConfig>;
  private projectilePool: ProjectilePool;
  private health: number;
  private isDestroyed: boolean = false;
  private lastFireTime: number = 0;

  // Movement
  private preferredDistance: number = 15; // Keep distance from target

  // Turret rotation
  private turretGroup!: THREE.Group;
  private turretRotation: number = 0;

  // Weak point (rear armor)
  private rearHitbox!: THREE.Mesh;

  constructor(
    scene: THREE.Scene,
    projectilePool: ProjectilePool,
    config: TankConfig
  ) {
    this.id = `tank_${tankIdCounter++}`;
    this.config = { ...DEFAULT_CONFIG, ...config } as Required<TankConfig>;
    this.projectilePool = projectilePool;
    this.health = this.config.health;

    // Create mesh
    this.mesh = this.createTankMesh();
    this.mesh.position.copy(config.position);
    this.mesh.position.y = 0.5;
    scene.add(this.mesh);

    // Set up hit detection
    this.mesh.userData = {
      id: this.id,
      type: 'tank',
      health: this.health,
      hitRadius: 2.5,
      points: 500,
      isEnemy: true,
      entity: this,
    };
  }

  private createTankMesh(): THREE.Group {
    const group = new THREE.Group();

    // Tank body - heavy armored hull
    const hullGeom = new THREE.BoxGeometry(3, 1.2, 4);
    const hullMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a2e,
      metalness: 0.9,
      roughness: 0.3,
    });
    const hull = new THREE.Mesh(hullGeom, hullMat);
    hull.position.y = 0.6;
    group.add(hull);

    // Angled front armor
    const frontArmorGeom = new THREE.BoxGeometry(3, 0.8, 0.5);
    const frontArmor = new THREE.Mesh(frontArmorGeom, hullMat);
    frontArmor.position.set(0, 0.8, 2);
    frontArmor.rotation.x = -0.4;
    group.add(frontArmor);

    // Treads (left)
    const treadGeom = new THREE.BoxGeometry(0.6, 0.8, 4.5);
    const treadMat = new THREE.MeshStandardMaterial({
      color: 0x0a0a1a,
      metalness: 0.7,
      roughness: 0.6,
    });
    const leftTread = new THREE.Mesh(treadGeom, treadMat);
    leftTread.position.set(-1.5, 0.4, 0);
    group.add(leftTread);

    // Treads (right)
    const rightTread = new THREE.Mesh(treadGeom, treadMat);
    rightTread.position.set(1.5, 0.4, 0);
    group.add(rightTread);

    // Turret base
    this.turretGroup = new THREE.Group();
    const turretBaseGeom = new THREE.CylinderGeometry(1, 1.2, 0.6, 8);
    const turretMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.9,
      roughness: 0.2,
    });
    const turretBase = new THREE.Mesh(turretBaseGeom, turretMat);
    this.turretGroup.add(turretBase);

    // Turret top
    const turretTopGeom = new THREE.BoxGeometry(1.5, 0.8, 2);
    const turretTop = new THREE.Mesh(turretTopGeom, turretMat);
    turretTop.position.set(0, 0.5, 0);
    this.turretGroup.add(turretTop);

    // Main cannon
    const cannonGeom = new THREE.CylinderGeometry(0.2, 0.25, 3, 8);
    const cannonMat = new THREE.MeshStandardMaterial({
      color: 0x3a3a5e,
      metalness: 0.95,
      roughness: 0.1,
    });
    const cannon = new THREE.Mesh(cannonGeom, cannonMat);
    cannon.rotation.x = Math.PI / 2;
    cannon.position.set(0, 0.5, 2);
    cannon.name = 'cannon';
    this.turretGroup.add(cannon);

    // Muzzle brake
    const muzzleGeom = new THREE.CylinderGeometry(0.3, 0.25, 0.3, 8);
    const muzzle = new THREE.Mesh(muzzleGeom, cannonMat);
    muzzle.rotation.x = Math.PI / 2;
    muzzle.position.set(0, 0.5, 3.5);
    muzzle.name = 'muzzle';
    this.turretGroup.add(muzzle);

    this.turretGroup.position.y = 1.4;
    group.add(this.turretGroup);

    // Glowing eye/sensor
    const sensorGeom = new THREE.SphereGeometry(0.15, 8, 8);
    const sensorMat = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 2,
    });
    const sensor = new THREE.Mesh(sensorGeom, sensorMat);
    sensor.position.set(0, 0.7, 1);
    this.turretGroup.add(sensor);

    // Weak point indicator (glowing rear exhaust)
    const weakPointGeom = new THREE.BoxGeometry(1.5, 0.5, 0.3);
    const weakPointMat = new THREE.MeshStandardMaterial({
      color: 0xff4400,
      emissive: 0xff4400,
      emissiveIntensity: 1,
    });
    this.rearHitbox = new THREE.Mesh(weakPointGeom, weakPointMat);
    this.rearHitbox.position.set(0, 0.6, -2.2);
    this.rearHitbox.name = 'weakpoint';
    group.add(this.rearHitbox);

    // Side vents
    const ventGeom = new THREE.BoxGeometry(0.1, 0.3, 1);
    const ventMat = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 0.5,
    });
    for (let i = 0; i < 3; i++) {
      const leftVent = new THREE.Mesh(ventGeom, ventMat);
      leftVent.position.set(-1.55, 0.8, -1 + i * 1);
      group.add(leftVent);

      const rightVent = new THREE.Mesh(ventGeom, ventMat);
      rightVent.position.set(1.55, 0.8, -1 + i * 1);
      group.add(rightVent);
    }

    return group;
  }

  public update(dt: number): void {
    if (this.isDestroyed) return;

    const target = this.config.targetRef;
    if (!target) return;

    const targetPos = target.position.clone();
    const myPos = this.mesh.position.clone();
    const toTarget = targetPos.clone().sub(myPos);
    const distToTarget = toTarget.length();
    toTarget.y = 0;

    // Movement AI - maintain preferred distance
    if (distToTarget > this.preferredDistance + 5) {
      // Move towards target
      const moveDir = toTarget.clone().normalize();
      this.mesh.position.addScaledVector(moveDir, this.config.speed * dt);
    } else if (distToTarget < this.preferredDistance - 5) {
      // Back away from target
      const moveDir = toTarget.clone().normalize();
      this.mesh.position.addScaledVector(moveDir, -this.config.speed * 0.5 * dt);
    }

    // Rotate hull towards target (slowly)
    if (toTarget.length() > 0.5) {
      const targetAngle = Math.atan2(toTarget.x, toTarget.z);
      let currentAngle = this.mesh.rotation.y;

      // Smooth rotation
      let angleDiff = targetAngle - currentAngle;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

      this.mesh.rotation.y += angleDiff * dt * 1.5;
    }

    // Rotate turret to track target independently (faster)
    const turretToTarget = targetPos.clone().sub(
      this.mesh.position.clone().add(new THREE.Vector3(0, 1.4, 0))
    );
    const turretTargetAngle = Math.atan2(turretToTarget.x, turretToTarget.z) - this.mesh.rotation.y;
    let turretAngleDiff = turretTargetAngle - this.turretRotation;
    while (turretAngleDiff > Math.PI) turretAngleDiff -= Math.PI * 2;
    while (turretAngleDiff < -Math.PI) turretAngleDiff += Math.PI * 2;

    this.turretRotation += turretAngleDiff * dt * 3;
    this.turretGroup.rotation.y = this.turretRotation;

    // Attack if in range and facing target
    if (distToTarget < 30 && Math.abs(turretAngleDiff) < 0.3) {
      this.tryFire(targetPos);
    }
  }

  private tryFire(targetPos: THREE.Vector3): void {
    const now = performance.now();
    const fireInterval = 1000 / this.config.fireRate;

    if (now - this.lastFireTime < fireInterval) return;

    this.lastFireTime = now;

    // Get muzzle position in world space
    const muzzle = this.turretGroup.getObjectByName('muzzle');
    if (!muzzle) return;

    const muzzlePos = new THREE.Vector3();
    muzzle.getWorldPosition(muzzlePos);

    // Direction to target with slight upward arc for dramatic effect
    const direction = targetPos.clone().sub(muzzlePos);
    direction.y += direction.length() * 0.05; // Slight arc
    direction.normalize();

    // Fire powerful projectile
    this.projectilePool.spawn(
      muzzlePos,
      direction,
      30, // slower but heavier projectile
      this.config.damage,
      new THREE.Color(0xff8800)
    );

    // Emit firing event for effects
    EventBus.emit('weapon:fired', {
      weaponType: 'Tank Cannon',
      ammoLeft: -1,
      muzzlePosition: { x: muzzlePos.x, y: muzzlePos.y, z: muzzlePos.z },
      direction: { x: direction.x, y: direction.y, z: direction.z },
    });
  }

  public takeDamage(damage: number, hitPosition?: THREE.Vector3): void {
    if (this.isDestroyed) return;

    let actualDamage = damage;

    // Check if hit from behind (weak point)
    if (hitPosition) {
      const toHit = hitPosition.clone().sub(this.mesh.position);
      const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(this.mesh.quaternion);
      const dot = toHit.normalize().dot(forward);

      // Hit from behind (dot < -0.5) = weak point, double damage
      if (dot < -0.5) {
        actualDamage *= 2.5;
        // Flash weak point
        this.flashWeakPoint();
      }
    }

    this.health -= actualDamage;
    this.mesh.userData.health = this.health;

    // Emit damage event
    EventBus.emit('target:damaged', {
      id: this.id,
      damage: actualDamage,
      healthRemaining: this.health,
    });

    // Flash effect
    this.flashDamage();

    if (this.health <= 0) {
      this.destroy();
    }
  }

  private flashDamage(): void {
    const turretBase = this.turretGroup.children[0] as THREE.Mesh;
    if (turretBase && turretBase.material) {
      const mat = turretBase.material as THREE.MeshStandardMaterial;
      const originalColor = mat.emissive.clone();
      mat.emissive.setHex(0xffffff);
      mat.emissiveIntensity = 2;

      setTimeout(() => {
        mat.emissive.copy(originalColor);
        mat.emissiveIntensity = 0;
      }, 100);
    }
  }

  private flashWeakPoint(): void {
    const mat = this.rearHitbox.material as THREE.MeshStandardMaterial;
    const originalIntensity = mat.emissiveIntensity;
    mat.emissiveIntensity = 5;

    setTimeout(() => {
      mat.emissiveIntensity = originalIntensity;
    }, 150);
  }

  private destroy(): void {
    this.isDestroyed = true;

    // Emit destroyed event
    EventBus.emit('target:destroyed', {
      id: this.id,
      position: {
        x: this.mesh.position.x,
        y: this.mesh.position.y,
        z: this.mesh.position.z,
      },
      points: this.mesh.userData.points,
    });

    // Remove mesh
    if (this.mesh.parent) {
      this.mesh.parent.remove(this.mesh);
    }
  }

  public isAlive(): boolean {
    return !this.isDestroyed;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }
}
